/* eslint-disable @typescript-eslint/naming-convention */
import { v4 as uuid } from 'uuid';
import { makeAutoObservable, runInAction } from 'mobx';
import { AxiosError, isAxiosError } from 'axios';
import { ApiResponseStatus } from '../constants/server';
import agent from '../api/agent';
import { store } from './store';
import {
  IDeliveryLimitations,
  RouteNotFoundError,
} from '../models/parcelDimensions';
import {
  CountryDetails,
  DeliveryOptionsRequest,
  ParcelDimensions,
} from '../models/shipmentCountryInfo';
import { Measure } from '../utils/parcel-measurements.ts/parcel-measurements-utils';
import { DeliveryOption } from '../models/delivery-options/delivery-option';
import { translate } from '../../i18n';
import { DeliveryMethod, PriceRange } from '../models/delivery-options/common';
import { getPrice } from '../routes/parcel-delivery/helpers';
import { ApiResponse } from '../models/apiResponse';

export interface ICalculatorData {
  countryDeparture: {
    countryCode: string;
    zipCode?: string;
  };
  countryDestination: {
    countryCode: string;
    zipCode?: string;
  };
  parcelDimensions: ParcelDimensions;
  measures?: Measure;
}

export default class WidgetCalculatorStore {
  countryDestination: CountryDetails | null = null;

  countryDeparture: CountryDetails | null = null;

  deliveryLimitations: IDeliveryLimitations | undefined;

  routeNotFoundError: RouteNotFoundError | undefined = undefined;

  deliveryOptions: DeliveryOption[] | undefined = undefined;

  groupedDeliveryPrices: Record<DeliveryMethod, PriceRange> | undefined =
    undefined;

  staticTimeStrings: Record<DeliveryMethod, string> | undefined = undefined;

  isLoadingLimitations: boolean = false;

  isCalculating: boolean = false;

  parcelDimensions: ParcelDimensions | null = null;

  showEstimatedDeliveryCost: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  setShowEstimatedDeliveryCost = (value: boolean) => {
    this.showEstimatedDeliveryCost = value;
  };

  makeShipmentCalculation = async (
    request: DeliveryOptionsRequest,
    allowRetryWithCapitalZip: boolean
  ) => {
    this.setIsCalculating(true);
    let errorMessage: string | ApiResponseStatus | undefined;
    try {
      const res = await agent.Calculator.deliveryOptionsV2(request);

      if (!res.data) return;

      const { miles, grouped_prices, static_time_strings } = res.data;

      miles.forEach((x) => {
        x.uiKey = uuid();
      });

      runInAction(() => {
        this.deliveryOptions = miles;
        this.groupedDeliveryPrices = grouped_prices;
        this.staticTimeStrings = static_time_strings;

        if (miles.length === 0) {
          throw new Error(
            "Info about first miles wasn't obtained from the API!"
          );
        }
      });
    } catch (error) {
      if (allowRetryWithCapitalZip && error instanceof AxiosError) {
        if (error.response?.status === ApiResponseStatus.VALIDATION_ERROR) {
          // in case 422 error is obtained then try with capital city zip
          errorMessage = ApiResponseStatus.VALIDATION_ERROR;
          return errorMessage;
        }
      }
      errorMessage = translate('delivery_options_not_found');
    } finally {
      this.setIsCalculating(false);
    }
    return errorMessage;
  };

  findShipmentCountry = (countryCode: string, countries?: CountryDetails[]) => {
    const countriesToSearchIn =
      countries ?? store.shipmentCountriesStore.senderCountries;

    return countriesToSearchIn?.find((c) => c.code === countryCode);
  };

  getDeliveryLimitations = async (
    departure: CountryDetails | null,
    destination: CountryDetails | null,
    allowRetryWithCapitalZip: boolean
  ) => {
    let errorMessage: string | undefined;
    if (!departure || !destination) return 'No route info provided!';

    runInAction(() => {
      this.isLoadingLimitations = true;
    });

    const getLimitations =
      store.commonStore.isGuestParcelCreation || !store.userStore.user
        ? agent.Guest.guestDeliveryLimitations
        : agent.Shipment.deliveryLimitations;

    try {
      const getLimitationsRequest = {
        shipment_type: '',
        departure_country: departure.code,
        departure_zip: departure.zipCode ?? '',
        destination_country: destination.code,
        destination_zip: destination.zipCode ?? '',
      };
      const limitations = await getLimitations(getLimitationsRequest);

      runInAction(() => {
        this.deliveryLimitations = limitations;
        this.routeNotFoundError = undefined;
      });
    } catch (error) {
      if (
        allowRetryWithCapitalZip &&
        isAxiosError(error) &&
        error.response?.status === ApiResponseStatus.VALIDATION_ERROR &&
        // Only retry in case we've not already tried with capital zip
        departure.zipCode !==
          store.shipmentCountriesStore.getCapital(departure.code)?.default_zip
      ) {
        // in case 422 error is obtained then try with capital city zip
        return ApiResponseStatus.VALIDATION_ERROR.toString();
      }

      if (isAxiosError<ApiResponse<RouteNotFoundError>>(error)) {
        const routeNotFoundError = error.response?.data.data;
        runInAction(() => {
          this.routeNotFoundError = routeNotFoundError;
          this.deliveryLimitations = undefined;
        });
        // No need to return error. We just need to show unsupported route modal
        return;
      }
      runInAction(() => {
        this.deliveryLimitations = undefined;
      });
      errorMessage = translate('delivery_options_not_found');
      return errorMessage;
    } finally {
      runInAction(() => {
        this.isLoadingLimitations = false;
      });
    }
  };

  setDepartureCountry = (country: CountryDetails | null) => {
    this.countryDeparture = country;
  };

  setDestinationCountry = (country: CountryDetails | null) => {
    this.countryDestination = country;
  };

  setParcelDimensions = (dimensions: ParcelDimensions | null) => {
    this.parcelDimensions = dimensions;
  };

  setIsCalculating = (isCalculating: boolean) => {
    this.isCalculating = isCalculating;
  };

  get cheapestPerMethod() {
    const result = {} as Record<DeliveryMethod, number>;

    this.deliveryOptions?.forEach((opt) => {
      const method = opt.middle_mile.delivery_method;
      const price = getPrice(opt);

      if (result[method] === undefined || price < result[method]) {
        result[method] = price;
      }
    });

    // {
    //   sea: 6,
    //   ground: 10,
    //   air: 12,
    //   expedited: 20
    // }

    return (Object.keys(result) as Array<DeliveryMethod>).sort(
      (a, b) => result[a] - result[b]
    );
  }

  resetStore = ({ departureCountry }: ResetStoreOptions = {}) => {
    if (departureCountry !== undefined) {
      this.countryDeparture = departureCountry;
    }
    this.countryDestination = null;
    this.deliveryLimitations = undefined;
    this.isCalculating = false;
    this.deliveryOptions = undefined;
    this.groupedDeliveryPrices = undefined;
    this.staticTimeStrings = undefined;
    this.parcelDimensions = null;
    this.showEstimatedDeliveryCost = false;
    this.isLoadingLimitations = false;
    this.routeNotFoundError = undefined;
  };
}

interface ResetStoreOptions {
  departureCountry?: CountryDetails | null;
  preserveShipmentCountries?: boolean;
}
